import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AddBuyerAddress from '@components/AddBuyerAddress';
import NoData from '@components/NoData';
import TimeBasedLoader from '@components/TimeBasedLoader';
import { toastError } from '@components/Toast';
import LoaderComponent from '@components/LoaderComponent';
import CommonImage from '@components/CommonImage';

import OrderWiseSupplier from '@features/order/components/OrderWiseSupplier';
import PaymentInfo from '@features/order/components/factory-po/PaymentInfo';
import ImportantNote from '@features/order/components/factory-po/ImportantNote';
import SupplierDesigns from '@features/order/components/factory-po/SupplierDesigns';
import {
	useLazyDownloadFactoryPOQuery,
	useLazyFetchFactoryPOQuery,
	useUpdateFactoryPOMutation
} from '@features/order/api/OrderDetailsApi';

import { fetchBeneficiaryList as fetchBuyerBillingLits, fetchBrandLocaionList } from '@store/orders/actions';
import {
	getBeneficiaryList as getBuyerBillingLits,
	getBrandLocationList,
	getInvoiceDetails,
	getSalesContractDetails
} from '@store/reducers';
import { fetchSalesContractDetails } from '@store/salesContract/actions';

import ApiClient from '@lib/apiClient';

const locationTypes = [
	{ id: 1, name: 'Factory location', type: 'FACTORY' },
	{ id: 2, name: 'Office location', type: 'OFFICE' }
];

const FactoryPO = ({ orderId }) => {
	const [isLoading] = useState(false);
	const [isMaterialListLoading, setIsMaterialListLoading] = useState(false);
	const [supplierList, setSupplierList] = useState([]);
	const [, setInitialLoading] = useState(false);
	const [factoryPODetails, setFactoryPODetailsDetails] = useState(null);
	const [startLoader, setStartLoader] = useState(false);
	const [isAddAddress, setIsAddAddress] = useState(false);
	const dispatch = useDispatch();
	let factoryPOInfoInfo = useSelector(getSalesContractDetails);
	let buyerBillingLits = useSelector(getBuyerBillingLits);
	const location = useLocation();
	let brandLocationList = useSelector(getBrandLocationList);
	let invoiceDetails = useSelector(getInvoiceDetails);
	const [fetchTrigger, { data }] = useLazyFetchFactoryPOQuery();
	const [downloadTrigger] = useLazyDownloadFactoryPOQuery();
	const [updateFactoryPO] = useUpdateFactoryPOMutation();

	const getSalesContractDetailsInfo = (id) => {
		setInitialLoading(true);
		fetchTrigger(id);
		dispatch(fetchSalesContractDetails(id)).finally(() => {
			setInitialLoading(false);
		});
	};

	useEffect(() => {
		setInitialLoading(true);
		dispatch(fetchBuyerBillingLits()).finally(() => {
			setInitialLoading(false);
		});
		getSupplierList(orderId, true);
	}, [orderId]);

	useEffect(() => {
		if (factoryPOInfoInfo?.id) {
			setFactoryPODetailsDetails(factoryPOInfoInfo);
			dispatch(fetchBrandLocaionList(factoryPOInfoInfo?.brandId));
		}
	}, [factoryPOInfoInfo, factoryPOInfoInfo?.id]);

	const getSupplierList = async (id, isInitialRender = false) => {
		setIsMaterialListLoading(true);
		await ApiClient.GET('getAllSupplierList', id)
			.then(({ data }) => {
				setIsMaterialListLoading(false);
				setSupplierList(data);
				if (isInitialRender) {
					let scId = new URLSearchParams(location.search).get('scId');
					if (scId) {
						return getSalesContractDetailsInfo(scId);
					}
					return getSalesContractDetailsInfo(data[0].id);
				}
			})
			.catch(({ response }) => {
				setIsMaterialListLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				}
			});
	};

	const onDownload = async () => {
		setStartLoader(true);
		const data = await downloadTrigger(factoryPODetails.id);
		window.open(data.error.data, '_blank');
		setStartLoader(false);
	};

	const onSalesContaractClick = (item) => {
		if (factoryPODetails.id !== item.id) {
			getSalesContractDetailsInfo(item.id);
		}
	};

	const onAddConsigneeAddress = () => {
		setIsAddAddress(!isAddAddress);
	};

	const getSelectedBuyerDetails = () => {
		return (
			brandLocationList.find((item) => item.id === data?.buyerBillingLocation?.id)?.title ||
			'Select address'
		);
	};

	const getSelectedBuyerShippingDetails = () => {
		return (
			brandLocationList.find((item) => item.id === data?.buyerShippingLocation?.id)?.title ||
			'Select address'
		);
	};
	const getSupplierAddress = () => {
		return (
			locationTypes.find((item) => item.type === data?.supplierLocation?.type)?.name || 'Select address'
		);
	};

	const onAddBuyerAddress = () => {
		setIsAddAddress(!isAddAddress);
	};

	return (
		<LoaderComponent loading={isLoading || isMaterialListLoading}>
			{supplierList.length > 0 && factoryPODetails && (
				<div className="create-order-container mt-5">
					<div className="factory-slider pt-2">
						<OrderWiseSupplier
							supplierList={supplierList}
							onClick={onSalesContaractClick}
							details={factoryPODetails}
							isShowStatus={false}
						/>
					</div>
					<div className="proforma-invoice-tab">
						<div className="section-heading d-flex justify-content-end align-items-center">
							<div className="order-assigned-people align-items-center">
								<button className="button gray-outline size32 mr-0" onClick={onDownload}>
									<CommonImage
										src={`/icons/download-dark-20.svg`}
										alt="download"
										className="mr-1"
									/>
									Download
								</button>
							</div>
						</div>
						<div className="invoice-info same-space d-flex ">
							<div className="d-flex invoice-left position-relative">
								<div className="sales-contract-heading">
									<h3 className="gray_dark_01 mt-2">Factory PO</h3>
								</div>
							</div>
						</div>
						<div className="buyer-and-beneficiary-section with-title d-flex justify-content-between">
							<div className="one-half same-space">
								<div className="one-half-title">
									<h3 className="semibold-18 mb-0">Buyer Address</h3>
								</div>
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Buyer Address</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{getSelectedBuyerDetails()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												<li className="px-4 pb-3 pt-3">
													<a
														href={void 0}
														className
														onClick={() => onAddBuyerAddress()}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
															className="mr-2"
															alt="add"
														/>
														Add address
													</a>
												</li>

												{brandLocationList.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															updateFactoryPO({
																id: factoryPODetails.id,
																requestBody: {
																	buyerBillingLocationId: item.id
																}
															});
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">
										{data?.buyerBillingLocation?.addressLine1}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerBillingLocation?.addressLine2}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerBillingLocation?.city}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerBillingLocation?.state}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerBillingLocation?.countryName}
									</p>
								</div>
							</div>
							<div className="one-half same-space">
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Buyer shipping address</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{getSelectedBuyerShippingDetails()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												<li className="px-4 pb-3 pt-3">
													<a
														href={void 0}
														className
														onClick={() => onAddBuyerAddress()}
													>
														<img
															src={`${process.env.PUBLIC_URL}/icons/add_primary.svg`}
															className="mr-2"
															alt="add"
														/>
														Add address
													</a>
												</li>
												{brandLocationList.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															updateFactoryPO({
																id: factoryPODetails.id,
																requestBody: {
																	buyerShippingLocation: item.id
																}
															});
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">{invoiceDetails.brandName}</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerShippingLocation?.addressLine1}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerShippingLocation?.addressLine2}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerShippingLocation?.city}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerShippingLocation?.state}
									</p>
									<p className="regular-14 gray_dark_01">
										{data?.buyerShippingLocation?.countryName}
									</p>
								</div>
							</div>
						</div>
						<div className="buyer-and-beneficiary-section with-title d-flex justify-content-between">
							<div className="one-half same-space">
								<div className="one-half-title">
									<h3 className="semibold-18 mb-0">NITEX & Supplier Address</h3>
								</div>
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">NITEX Address</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{data?.nitexAddress?.title || 'Select address'}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												{buyerBillingLits.map((item, index) => (
													<li
														className="px-4 pb-3 pt-3"
														key={factoryPODetails.id + index}
														onClick={() => {
															updateFactoryPO({
																id: factoryPODetails.id,
																requestBody: {
																	nitexAddress: item
																}
															});
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.title}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">{data?.nitexAddress?.name}</p>
									<p className="regular-14 gray_dark_01">{data?.nitexAddress?.address}</p>
									<p className="regular-14 gray_dark_01">{data?.nitexAddress?.website}</p>
								</div>
							</div>
							<div className="one-half same-space">
								<div className="info-heading d-flex justify-content-between">
									<p className="semibold-14 mb-0">Supplier Address</p>
									<div className="option">
										<div className="dropdown">
											<button
												className="btn btn-default dropdown-toggle gray_dark_02 regular-14 pt-0"
												type="button"
												data-toggle="dropdown"
												aria-expanded="false"
											>
												{getSupplierAddress()}
												<img
													src={`${process.env.PUBLIC_URL}/icons/down-arrow.png`}
													className="ml-2"
													alt="down"
												/>
											</button>
											<ul className="dropdown-menu dropdown-menu-right shadow-4dp">
												{locationTypes.map((item) => (
													<li
														className="px-4 pb-3 pt-3"
														key={item.id}
														onClick={() => {
															updateFactoryPO({
																id: factoryPODetails.id,
																requestBody: {
																	supplierLocation: item.id
																}
															});
														}}
													>
														<a href={void 0} className="gray_dark_02">
															{item.name}
														</a>
													</li>
												))}
											</ul>
										</div>
									</div>
								</div>
								<div className="info-details">
									<p className="regular-14 gray_dark_01">{data?.supplierLocation?.title}</p>
									<p className="regular-14 gray_dark_01">
										{data?.supplierLocation?.addressLine1}
									</p>
								</div>
							</div>
						</div>
						<PaymentInfo factoryId={factoryPODetails?.id} factoryPODetails={data} />
						<ImportantNote factoryId={factoryPODetails?.id} factoryPODetails={data} />
						<SupplierDesigns factoryPODetails={data} />
					</div>
				</div>
			)}
			{!isMaterialListLoading && supplierList.length === 0 && (
				<div className="create-order-container">
					<NoData message={'No supplier found'} />
				</div>
			)}
			{isAddAddress && (
				<AddBuyerAddress onAddBuyerAddress={onAddConsigneeAddress} details={factoryPODetails} />
			)}
			{startLoader && <TimeBasedLoader startLoader={startLoader} />}
		</LoaderComponent>
	);
};

export default FactoryPO;
